import {
  API_TRANSPORT_UNKNOWN_HOST,
  API_TRANSPORT_USER_MESSAGE,
  ApiTransportError,
  AuthenticatedRequestUserMismatchError,
  AuthRefreshUnavailableError,
  AuthRequestError,
  apiRequestHost,
  apiRequestPath,
  apiTransportFingerprint,
  isAbortError,
  isApiTransportError,
  isAuthRefreshUnavailableError,
} from './apiErrors'
import { getAccessToken, refreshSession } from './auth'
import { AUTH_SESSION_INVALID_EVENT } from './authSessionEvents'
import { captureSentryError } from './sentry'
import { supabase } from './supabase'

export type AuthAction = 'refresh' | 'reauth' | 'none'

export interface ApiError {
  error: string
  code?: string
  message?: string
  authAction?: AuthAction
}

type RefreshedSession = { access_token: string; expires_at: number } | null
type RefreshSessionResult =
  | { status: 'refreshed'; session: NonNullable<RefreshedSession> }
  | { status: 'unavailable' }
  | { status: 'invalidated' }
type RefreshedRetryResult =
  { status: 'response'; response: Response } | Exclude<RefreshSessionResult, { status: 'refreshed' }>

let authRefreshInFlight: Promise<RefreshSessionResult> | null = null
let authInvalidationVersion = 0
let lastAuthInvalidationError: AuthSessionInvalidError | null = null

export type AuthenticatedRequestContext = {
  /** Refuse to send if the captured JWT belongs to a different account. This
      binds durable/background work to the user who originally queued it. */
  expectedUserId?: string
}

export class AuthSessionInvalidError extends Error {
  code: string
  authAction: AuthAction
  endpoint?: string

  constructor(message: string, options: { code?: string; authAction?: AuthAction; endpoint?: string } = {}) {
    super(message)
    this.name = 'AuthSessionInvalidError'
    this.code = options.code ?? 'AUTH_SESSION_INVALID'
    this.authAction = options.authAction ?? 'reauth'
    this.endpoint = options.endpoint
  }
}

export function isAuthSessionInvalidError(error: unknown): error is AuthSessionInvalidError {
  return error instanceof AuthSessionInvalidError
}

export {
  ApiTransportError,
  AuthenticatedRequestUserMismatchError,
  AuthRefreshUnavailableError,
  AuthRequestError,
  isApiTransportError,
  isAuthRefreshUnavailableError,
}

function fallbackAuthAction(errorData: ApiError): AuthAction | null {
  const message = `${errorData.message ?? ''} ${errorData.error ?? ''}`.toLowerCase()

  if (errorData.code === 'TOKEN_EXPIRED' || message.includes('expired')) {
    return 'refresh'
  }

  if (message.includes('auth session missing')) {
    return 'refresh'
  }

  if (
    errorData.code === 'TOKEN_INVALID' ||
    errorData.code === 'NO_USER' ||
    errorData.code === 'MISSING_TOKEN' ||
    message.includes('invalid jwt')
  ) {
    return 'reauth'
  }

  return null
}

function authActionFor(errorData: ApiError): AuthAction {
  return errorData.authAction ?? fallbackAuthAction(errorData) ?? 'none'
}

async function parseApiError(response: Response): Promise<ApiError> {
  const parsed = await response.json().catch(() => null)
  if (parsed && typeof parsed === 'object') {
    const body = parsed as Record<string, unknown>
    return {
      error: typeof body.error === 'string' ? body.error : response.statusText || 'Authentication failed',
      code: typeof body.code === 'string' ? body.code : undefined,
      message: typeof body.message === 'string' ? body.message : undefined,
      authAction:
        body.authAction === 'refresh' || body.authAction === 'reauth' || body.authAction === 'none'
          ? body.authAction
          : undefined,
    }
  }

  return {
    error: response.statusText || 'Authentication failed',
  }
}

function emitAuthSessionInvalid(error: AuthSessionInvalidError): void {
  authInvalidationVersion += 1
  authRefreshInFlight = null
  lastAuthInvalidationError = error

  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return

  window.dispatchEvent(
    new CustomEvent(AUTH_SESSION_INVALID_EVENT, {
      detail: {
        code: error.code,
        authAction: error.authAction,
        endpoint: error.endpoint,
        message: error.message,
      },
    }),
  )
}

function createAuthSessionInvalidError(errorData: ApiError, endpoint: string, authAction: AuthAction) {
  return new AuthSessionInvalidError(
    `Authentication session is invalid: ${errorData.message || errorData.error || 'Please sign in again.'}`,
    {
      code: errorData.code,
      authAction,
      endpoint,
    },
  )
}

function throwAuthSessionInvalid(errorData: ApiError, endpoint: string, authAction: AuthAction): never {
  const authError = createAuthSessionInvalidError(errorData, endpoint, authAction)
  emitAuthSessionInvalid(authError)
  throw authError
}

function throwCurrentAuthInvalidation(errorData: ApiError, endpoint: string): never {
  throw lastAuthInvalidationError ?? createAuthSessionInvalidError(errorData, endpoint, 'reauth')
}

function throwAuthRefreshUnavailable(errorData: ApiError): never {
  const code = errorData.code || 'REFRESH_FAILED'
  throw new AuthRefreshUnavailableError(
    `Authentication refresh failed: ${errorData.message || errorData.error || 'Unable to refresh your session.'}. ` +
      `Error code: ${code}. Please try again.`,
    code,
  )
}

function authenticatedHeaders(options: RequestInit, token: string): Record<string, string> {
  const headers: Record<string, string> = {}
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData

  if (!isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  new Headers(options.headers).forEach((value, key) => {
    headers[key] = value
  })
  headers.Authorization = `Bearer ${token}`
  return headers
}

function jwtSubject(token: string): string | null {
  try {
    const encoded = token.split('.')[1]
    if (!encoded) return null
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const payload = JSON.parse(globalThis.atob(padded)) as { sub?: unknown }
    return typeof payload.sub === 'string' ? payload.sub : null
  } catch {
    return null
  }
}

function assertExpectedUser(token: string, expectedUserId: string | undefined): void {
  if (expectedUserId && jwtSubject(token) !== expectedUserId) {
    throw new AuthenticatedRequestUserMismatchError(expectedUserId)
  }
}

async function hasCachedSession(): Promise<boolean> {
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession()
  // auth-js answers `session: null` *with an error* when storage holds a
  // session whose refresh just failed — the browser came back online and the
  // first refresh died on the network flap. That is a session we could not
  // refresh, not the absence of one: reporting it as MISSING_TOKEN classed a
  // transient outage as "signed out" (Sentry MARTINI-VERCEL-NEXT-1BA).
  return Boolean(session) || Boolean(error)
}

/**
 * One refresh shared by every request that got a refreshable 401 at the same
 * time. `forUserId` is the user whose token was rejected: it lets a refresh
 * that lost a race to another tab's refresh adopt the stored session, and
 * only when that session is still the same user's (see `refreshSession`).
 */
async function refreshSessionForApi(forUserId: string | undefined): Promise<RefreshSessionResult> {
  if (!authRefreshInFlight) {
    const startedAtInvalidationVersion = authInvalidationVersion
    authRefreshInFlight = refreshSession({ forUserId })
      .then(session => {
        if (authInvalidationVersion !== startedAtInvalidationVersion) {
          return { status: 'invalidated' as const }
        }
        if (!session) {
          return { status: 'unavailable' as const }
        }
        return { status: 'refreshed' as const, session }
      })
      .finally(() => {
        authRefreshInFlight = null
      })
  }
  return authRefreshInFlight
}

function wrapTransportError(error: unknown, url: string, attempts: number): ApiTransportError {
  if (isApiTransportError(error)) return error

  const requestPath = apiRequestPath(url)
  const requestHost = apiRequestHost(url)
  const wrapped = new ApiTransportError(API_TRANSPORT_USER_MESSAGE, {
    endpoint: url,
    requestPath,
    requestHost,
    attempts,
    cause: error,
  })

  captureSentryError(wrapped, {
    operation: 'api_transport_error',
    tags: {
      request_path: requestPath,
      error_kind: 'transport',
      transport_attempts: String(attempts),
      // Always set, and to the same value the fingerprint uses, so the tag you
      // filter on and the group key never disagree for a hostless request.
      request_host: requestHost ?? API_TRANSPORT_UNKNOWN_HOST,
    },
    extra: { url },
    fingerprint: apiTransportFingerprint(requestHost),
  })
  wrapped.reportedToSentry = true

  return wrapped
}

/**
 * Backoff between transport retries. Two extra attempts covers the shape these
 * failures actually have in production — short bursts where a whole page's API
 * fan-out fails at once and the host answers again seconds later.
 */
export const TRANSPORT_RETRY_BACKOFF_MS = [300, 1200] as const

/**
 * Only reads are replayed. A POST/PATCH/DELETE that died in transport may still
 * have reached the backend, so re-sending it could duplicate the write.
 */
function isIdempotentRequest(init: RequestInit): boolean {
  const method = (init.method ?? 'GET').toUpperCase()
  return method === 'GET' || method === 'HEAD'
}

function delay(ms: number, signal: AbortSignal | null | undefined): Promise<void> {
  return new Promise(resolve => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    function onAbort() {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

/**
 * Fetch, retrying transport-level rejections on idempotent requests before the
 * failure ever reaches the caller or Sentry. A request that succeeds on a retry
 * is not an error — it produces no Sentry event, only a console warning.
 *
 * Exported for fetches that cannot go through `authenticatedRequest` — a
 * presigned R2 PUT must not carry the Supabase JWT — but should still fail as
 * `ApiTransportError`: reported once here at the choke point, grouped per host
 * (ENG-749), and skipped by any `captureSentryError` in a caller's catch
 * block. A bare `fetch` at such a call site leaks raw browser TypeErrors into
 * Sentry as their own per-operation issues.
 */
export async function fetchOrThrowTransport(url: string, init: RequestInit): Promise<Response> {
  const maxAttempts = isIdempotentRequest(init) ? TRANSPORT_RETRY_BACKOFF_MS.length + 1 : 1

  for (let attempt = 1; ; attempt++) {
    try {
      const response = await fetch(url, init)
      if (attempt > 1) {
        console.warn(`[API] Transport failure recovered after ${attempt} attempts: ${apiRequestPath(url)}`)
      }
      return response
    } catch (error) {
      if (isAbortError(error)) throw error
      if (attempt >= maxAttempts) throw wrapTransportError(error, url, attempt)
      await delay(TRANSPORT_RETRY_BACKOFF_MS[attempt - 1], init.signal)
    }
  }
}

function isPassthroughRequestError(error: unknown): boolean {
  return (
    error instanceof AuthenticatedRequestUserMismatchError ||
    isAuthSessionInvalidError(error) ||
    isAuthRefreshUnavailableError(error) ||
    isApiTransportError(error) ||
    (error instanceof Error && error.message.startsWith('Authentication'))
  )
}

async function retryWithRefreshedAuth(
  url: string,
  options: RequestInit,
  expectedUserId: string | undefined,
  sessionUserId: string | undefined,
): Promise<RefreshedRetryResult> {
  console.log('[API] Auth token rejected, attempting refresh and retry...')
  const result = await refreshSessionForApi(sessionUserId)
  if (result.status !== 'refreshed') return result

  // Concurrent 401s share one in-flight refresh. A request that went out as
  // user B may therefore receive a result produced for user A (another
  // account signed in meanwhile). The retry is bound to the user of the token
  // it originally sent — the caller's `expectedUserId` when given, else the
  // subject of that token — so it can never be replayed as someone else.
  assertExpectedUser(result.session.access_token, expectedUserId ?? sessionUserId)
  const newHeaders = authenticatedHeaders(options, result.session.access_token)

  return { status: 'response', response: await fetchOrThrowTransport(url, { ...options, headers: newHeaders }) }
}

export async function authenticatedRequest(
  url: string,
  options: RequestInit = {},
  context: AuthenticatedRequestContext = {},
): Promise<Response> {
  const token = await getAccessToken()
  if (!token) {
    if (await hasCachedSession()) {
      throwAuthRefreshUnavailable({
        error: 'Authentication refresh failed',
        code: 'TOKEN_REFRESH_FAILED',
        message: 'Unable to refresh the current session.',
        authAction: 'none',
      })
    }

    // No session at all: nothing exists to invalidate, so this fails the
    // caller without broadcasting AUTH_SESSION_INVALID_EVENT. The event tears
    // down AuthContext and signs out — correct when the backend rejects a live
    // token, wrong here, where the usual cause is a fetch that raced
    // `initAuth()` and would otherwise sign out the anonymous session being
    // created at that very moment.
    throw createAuthSessionInvalidError(
      {
        error: 'Authentication required',
        code: 'MISSING_TOKEN',
        message: 'No authentication token is available.',
        authAction: 'reauth',
      },
      url,
      'reauth',
    )
  }

  assertExpectedUser(token, context.expectedUserId)
  // The user this request went out as. A refresh retry below may only adopt a
  // session another tab stored if it belongs to this same user.
  const sessionUserId = context.expectedUserId ?? jwtSubject(token) ?? undefined
  const headers = authenticatedHeaders(options, token)

  const response = await fetchOrThrowTransport(url, { ...options, headers })

  if (response.status !== 401) {
    return response
  }

  try {
    const errorData = await parseApiError(response)
    const authAction = authActionFor(errorData)

    if (authAction === 'refresh') {
      const retryResult = await retryWithRefreshedAuth(url, options, context.expectedUserId, sessionUserId)

      if (retryResult.status === 'unavailable') {
        throwAuthRefreshUnavailable(errorData)
      }
      if (retryResult.status === 'invalidated') {
        throwCurrentAuthInvalidation(errorData, url)
      }

      const retryResponse = retryResult.response
      if (retryResponse.status !== 401) {
        return retryResponse
      }

      const retryError = await parseApiError(retryResponse)
      const retryAuthAction = authActionFor(retryError)

      // The token was just refreshed successfully, so a non-auth failure validating it
      // (e.g. a transient backend NETWORK_ERROR -> authAction 'none') must not sign the
      // user out. Surface a retryable error instead, mirroring the other 'none' paths.
      if (retryAuthAction === 'none') {
        throwAuthRefreshUnavailable(retryError)
      }

      const authError = createAuthSessionInvalidError(
        retryError,
        url,
        retryAuthAction === 'refresh' ? 'reauth' : retryAuthAction,
      )

      captureSentryError(authError, {
        operation: 'api_auth_retry_failed',
        tags: {
          endpoint: url,
          error_code: retryError.code || 'UNKNOWN',
          auth_action: retryError.authAction || retryAuthAction,
        },
      })
      emitAuthSessionInvalid(authError)
      throw authError
    }

    if (authAction === 'reauth') {
      throwAuthSessionInvalid(errorData, url, authAction)
    }

    if (
      errorData.code === 'NETWORK_ERROR' ||
      errorData.message?.includes('fetch failed') ||
      errorData.message?.includes('network')
    ) {
      try {
        console.log('[API] Backend network error validating token, retrying request...')
        await new Promise(resolve => setTimeout(resolve, 1000))
        const retryResponse = await fetchOrThrowTransport(url, { ...options, headers })

        if (retryResponse.ok || retryResponse.status !== 401) {
          return retryResponse
        }

        const retryError = await parseApiError(retryResponse)
        const retryAuthAction = authActionFor(retryError)
        if (retryAuthAction === 'refresh') {
          const refreshedRetry = await retryWithRefreshedAuth(url, options, context.expectedUserId, sessionUserId)
          if (refreshedRetry.status === 'unavailable') {
            throwAuthRefreshUnavailable(retryError)
          }
          if (refreshedRetry.status === 'invalidated') {
            throwCurrentAuthInvalidation(retryError, url)
          }
          if (refreshedRetry.response.status !== 401) {
            return refreshedRetry.response
          }
          const refreshedError = await parseApiError(refreshedRetry.response)
          throwAuthSessionInvalid(refreshedError, url, 'reauth')
        }
        if (retryAuthAction === 'reauth') {
          throwAuthSessionInvalid(retryError, url, retryAuthAction)
        }
      } catch (retryErr) {
        if (isPassthroughRequestError(retryErr)) {
          throw retryErr
        }
        captureSentryError(retryErr instanceof Error ? retryErr : new Error(String(retryErr)), {
          operation: 'api_network_retry_failed',
          tags: { endpoint: url },
        })
      }
    }

    const authError = new AuthRequestError(
      `Authentication error: ${errorData.message || errorData.error}. ` +
        `Error code: ${errorData.code || 'UNKNOWN'}. Please check your credentials or refresh the page.`,
    )

    captureSentryError(authError, {
      operation: 'api_auth_error',
      tags: {
        endpoint: url,
        error_code: errorData.code || 'UNKNOWN',
        auth_action: errorData.authAction || authAction,
      },
    })

    throw authError
  } catch (err) {
    if (isPassthroughRequestError(err)) {
      throw err
    }
    captureSentryError(err, {
      operation: 'api_auth_unexpected_error',
      tags: { endpoint: url },
    })
    const authError = new AuthRequestError(
      'Authentication failed with unexpected error format. Please refresh the page.',
    )
    authError.cause = err
    throw authError
  }
}
